import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { RocketPurpleAnimate } from "components/icons/RocketPurpleAnimate";
import { PlatformSubscriptionCancelModal } from "components/PlatformSubscriptionCancelModal";
import {
  BenefitPoint,
  SubscriptionInfo,
  VerticalRow,
} from "components/SubscriptionOverviewComponents";
import { BOOKS_PLUS_PLAN_MANAGE } from "constants/addons";
import { MM_SPACE_YYYY } from "constants/date";
import dayjs from "dayjs";
import { BILLING_CYCLE } from "dictionaries";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useHistory } from "react-router-dom";
import SmallSparkle from "static/images/SmallSparkle.svg";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { resetAllAddonDataPayload } from "store/slices/addons";
import { SubscriptionInProfile } from "types/Models/subscription";
import { capitalize } from "utils/capitalize";
import { formatDate } from "utils/formatDate";
import * as STRIPE_COUPON_TYPES from "constants/stripeCouponTypes";

export const booksAccessPoints = [
  "You’ll be limited to just 2 data connections",
  "Real-time syncing with your Stripe account will be turned off.",
  "Transactions will no longer be auto-categorized, manual work returns.",
  "You will no longer have access to a bookkeeping expert to maintain your books each month.",
];

const BookkeepingPlusPlanManage = () => {
  const dispatch = useAppDispatch();
  const group = useCurrentGroupContext();
  const groupId = group.uuid;
  const entityId = useCurrentEntityId();
  const { data: subscriptions } = useGetSubscriptionsQuery(
    {
      groupId,
      entityId,
    },
    {
      skip: !groupId || !entityId,
    }
  );
  const { addonData, addonType } = useAppSelector((state) => state.addonsSlice);
  const cancelationModal = useModal();
  const isOpen = addonType === BOOKS_PLUS_PLAN_MANAGE;
  const history = useHistory();

  const onClose = () => {
    dispatch(resetAllAddonDataPayload());
  };

  const currentSubscription = subscriptions?.find(
    (sub) => sub.uuid === addonData?.uuid
  );
  const currentGroupSubscription = currentSubscription?.group_subscription?.[0];

  const handleContactUs = () => {
    onClose();
    history.push("/chat");
  };

  const handleSubscriptionClose = () => {
    onClose();
    cancelationModal.close();
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Modal.Content>
          <Modal.Header className="t-bg-purple-0 t-flex t-justify-between t-rounded-tl-lg t-rounded-tr-lg !t-pl-6">
            <div className="t-flex t-items-center t-gap-4">
              <RocketPurpleAnimate />
              <div>
                <div className="t-text-subtext t-text-text-100">
                  {currentSubscription?.subscription_name}
                </div>
                {addonData && (
                  <div className="t-flex t-items-center t-gap-1.5">
                    <span className="t-text-subtext-sm t-text-neutral-80">
                      <AmountSuperScript amount={Number(addonData.amount)} />
                      <span>/{BILLING_CYCLE[addonData.billing_cycle]}</span>
                    </span>
                    {addonData.stripe_coupon && (
                      <span className="t-text-body-sm t-flex t-gap-0.5 t-text-dark_green-70 t-px-1.5 t-py-0.5 t-bg-dark_green-10 t-rounded-2xl">
                        {addonData.stripe_coupon.discount_type ===
                        STRIPE_COUPON_TYPES.PERCENT
                          ? `${addonData.stripe_coupon.discount_rate}%`
                          : `$${addonData.stripe_coupon.discount_rate}`}{" "}
                        Off with {addonData.stripe_coupon?.coupon_code}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-px-0">
            <div className="">
              <div className="t-grid t-grid-cols-5 t-items-center t-gap-y-6 t-mb-6 t-overflow-hidden">
                <SubscriptionInfo
                  label="Started from"
                  value={
                    currentGroupSubscription?.started_on
                      ? dayjs(currentGroupSubscription.started_on).format(
                          MM_SPACE_YYYY
                        )
                      : "-"
                  }
                />
                <VerticalRow />
                {addonData && (
                  <>
                    <SubscriptionInfo
                      label="Frequency"
                      value={capitalize(addonData.billing_cycle)}
                    />
                    <VerticalRow />
                  </>
                )}
                <SubscriptionInfo
                  label="Next Billing Date"
                  value={
                    currentSubscription?.scheduled_date
                      ? formatDate(currentSubscription?.scheduled_date)
                      : "-"
                  }
                />
              </div>
              <div className="t-border t-border-solid t-border-purple-0 t-p-1 t-rounded-lg">
                <div className="t-flex t-items-center t-gap-2  t-px-3 t-py-1">
                  <img src={SmallSparkle} alt="SmallSparkle" />
                  <span className="t-text-body-sm t-text-text-60">
                    Plan benefits
                  </span>
                </div>
                <div className="t-rounded-lg t-bg-surface-lighter-grey t-space-y-1.5 t-p-3">
                  {currentSubscription?.metadata?.benefits?.map(
                    (feature, index) => (
                      <BenefitPoint key={index} description={feature} />
                    )
                  )}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={handleContactUs} customType="ghost">
              Contact us
            </Button>
            <Button onClick={cancelationModal.open}>Cancel subscription</Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>

      {currentSubscription &&
        currentGroupSubscription?.group_subscription_id && (
          <PlatformSubscriptionCancelModal
            isOpen={cancelationModal.isOpen}
            onClose={handleSubscriptionClose}
            AccessPoints={booksAccessPoints}
            planCancelLabel="Are you sure you want to cancel subscription?"
            handleCancelSubscription={handleSubscriptionClose}
            subscription={
              currentSubscription as unknown as SubscriptionInProfile
            }
            groupSubscriptionId={
              currentGroupSubscription?.group_subscription_id
            }
          />
        )}
    </>
  );
};

export default BookkeepingPlusPlanManage;
